import { Controller } from "@hotwired/stimulus"

// The disclosure graph, transliterated from the reference implementation attached to the
// Maturity Dashboard Technical Specification (the mockup's mountGraph). All styling lives
// in the dashboard design layer (app/assets/stylesheets/dashboard.css) under .dashboard-ui;
// this controller emits the reference's class names (gnodeg/edge/sel/down/hot, side-hd,
// sigrow, resrow, dl, scope) and toggles them exactly as the reference does.
//
// Renderer compatibility rules: node kinds and edge relations are open sets — unknown kinds
// get the kind name as their glyph and base styling, unknown relations the base edge style,
// the side panel has a generic branch, and every payload read is optional-with-default.
const SVG = "http://www.w3.org/2000/svg"
const NW = 158
const NH = 30
const COL_GAP = 205
const ROW_GAP = 46
const MX = 18
const MY = 22

const GLYPHS = {
  org: "org", security_txt: "txt", security_dns: "dns", security_page: "page",
  security_program: "program", contact: "contact", assets: "assets", directory: "directory",
}

const GROUP_LABELS = {
  security_txt: "security.txt", security_dns: "DNS records", security_page: "Security pages",
  security_program: "Programs", contact: "Contacts", assets: "Assets", directory: "Directories",
}

const STATE_CLASS = { verified: "v", guessed: "g", not_set: "n" }
const STATE_CHAR = { verified: "✓", guessed: "~", not_set: "" }
const STATE_LABEL = { verified: "verified", guessed: "inferred", not_set: "not set" }

export default class extends Controller {
  static targets = ["canvas", "panel"]
  static values = { graph: Object, name: String, level: String, status: String }

  connect() {
    this.nodes = (this.graphValue.nodes || []).filter(n => n && n.id)
    this.signals = this.nodes.filter(n => n.kind === "signal")
    this.aggregateAssets()
    this.orgNode = { id: "org", kind: "org", locator: this.nameValue || "Organization" }
    this.allEdges = this.buildEdges()
    this.adjacency = {}
    for (const e of this.allEdges) (this.adjacency[e.from] = this.adjacency[e.from] || []).push(e.to)
    this.panelTarget.innerHTML = `<div class="side-hd"></div><div class="side-bd"></div>`
    this.sideHd = this.panelTarget.querySelector(".side-hd")
    this.sideBd = this.panelTarget.querySelector(".side-bd")
    this.render()
    this.select("org")
    this.pinPanelHeight()
  }

  disconnect() {
    this.resizeObserver?.disconnect()
  }

  // The reference's height pin: the side panel takes the mapcard's height (which the graph
  // alone determines) and its body scrolls, so selecting a node with a tall panel never
  // resizes the canvas.
  pinPanelHeight() {
    const mapcard = this.canvasTarget.closest(".mapcard")
    if (!mapcard) return
    this.resizeObserver = new ResizeObserver(() => {
      this.panelTarget.style.height = `${mapcard.getBoundingClientRect().height}px`
    })
    this.resizeObserver.observe(mapcard)
  }

  // The reference aggregates assets: one dashed "N assets" node per covering source. Covers
  // edges rewrite onto the aggregate; assets nothing covers gather at the organization.
  aggregateAssets() {
    const rawEdges = this.graphValue.edges || []
    const assets = this.nodes.filter(n => n.kind === "asset")
    this.drawable = this.nodes.filter(n => n.kind !== "asset" && n.kind !== "signal")
    if (!assets.length) {
      this.storedEdges = rawEdges
      return
    }
    const assetIds = new Set(assets.map(n => n.id))
    const coverer = {}
    for (const e of rawEdges) {
      if (e.relation === "covers" && assetIds.has(e.to)) coverer[e.to] = e.from
    }
    const groups = {}
    for (const asset of assets) {
      const key = coverer[asset.id] || "org"
      ;(groups[key] = groups[key] || []).push(asset)
    }
    for (const [key, members] of Object.entries(groups)) {
      this.drawable.push({
        id: `agg:${key}`, kind: "assets", covererId: key,
        locator: `${members.length} asset${members.length === 1 ? "" : "s"}`, members,
      })
    }
    this.storedEdges = rawEdges
      .filter(e => !assetIds.has(e.from))
      .map(e => (assetIds.has(e.to) ? { ...e, to: `agg:${coverer[e.to] || "org"}` } : e))
      .filter((e, i, all) => all.findIndex(o => o.from === e.from && o.to === e.to && o.relation === e.relation) === i)
  }

  // Stored edges between drawable nodes, plus a synthetic owns edge from the organization to
  // every node with no inbound edge (ownership is not stored; the graph is org-scoped).
  buildEdges() {
    const ids = new Set(this.drawable.map(n => n.id))
    const stored = this.storedEdges
      .map(e => ({ from: e.from, to: e.to, relation: e.relation || "", data: e.data }))
      .filter(e => ids.has(e.from) && ids.has(e.to))
    const hasInbound = new Set(stored.map(e => e.to))
    const roots = this.drawable
      .filter(n => !hasInbound.has(n.id))
      .map(n => ({ from: "org", to: n.id, relation: "owns" }))
    return roots.concat(stored)
  }

  // The reference layout, verbatim: BFS tiers from the organization, then each tier's rows
  // ordered by the barycenter of their parents' positions.
  layout() {
    const nodes = [this.orgNode, ...this.drawable]
    const tier = { org: 0 }
    const queue = ["org"]
    while (queue.length) {
      const current = queue.shift()
      for (const next of this.adjacency[current] || []) {
        if (tier[next] == null) { tier[next] = tier[current] + 1; queue.push(next) }
      }
    }
    for (const n of nodes) if (tier[n.id] == null) tier[n.id] = 1

    const cols = {}
    for (const n of nodes) (cols[tier[n.id]] = cols[tier[n.id]] || []).push(n)

    const parents = {}
    for (const e of this.allEdges) (parents[e.to] = parents[e.to] || []).push(e.from)
    const original = {}
    nodes.forEach((n, i) => { original[n.id] = i })
    const index = {}
    for (const t of Object.keys(cols).map(Number).sort((a, b) => a - b)) {
      const arr = cols[t]
      if (t > 0) {
        const bary = (n) => {
          const ps = (parents[n.id] || []).map(p => index[p]).filter(v => v != null)
          return ps.length ? ps.reduce((s, v) => s + v, 0) / ps.length : 1e9
        }
        arr.sort((a, b) => bary(a) - bary(b) || original[a.id] - original[b.id])
      }
      arr.forEach((n, i) => { index[n.id] = i })
    }

    const maxTier = Math.max(...nodes.map(n => tier[n.id]))
    const maxRows = Math.max(1, ...Object.values(cols).map(c => c.length))
    const W = MX * 2 + maxTier * COL_GAP + NW
    const H = MY * 2 + (maxRows - 1) * ROW_GAP + NH
    const pos = {}
    for (const t of Object.keys(cols)) {
      const arr = cols[t]
      const colH = (arr.length - 1) * ROW_GAP
      const y0 = (H - NH) / 2 - colH / 2
      arr.forEach((n, i) => { pos[n.id] = { x: MX + t * COL_GAP, y: y0 + i * ROW_GAP } })
    }
    return { pos, W, H }
  }

  render() {
    const { pos, W, H } = this.layout()
    this.svg = this.el("svg", { class: "graph", viewBox: `0 0 ${W} ${H}`, width: W, height: H, role: "img" })
    const edgeGroup = this.el("g")
    const nodeGroup = this.el("g")
    this.svg.append(edgeGroup, nodeGroup)

    this.edgeEls = []
    for (const edge of this.allEdges) {
      const a = pos[edge.from], b = pos[edge.to]
      if (!a || !b) continue
      const x1 = a.x + NW, y1 = a.y + NH / 2, x2 = b.x, y2 = b.y + NH / 2, mx = (x1 + x2) / 2
      const path = this.el("path", {
        class: `edge ${edge.relation}`,
        d: `M${x1},${y1} C${mx},${y1} ${mx},${y2} ${x2},${y2}`,
      })
      edgeGroup.append(path)
      this.edgeEls.push({ edge, path })
    }

    this.nodeEls = {}
    for (const node of [this.orgNode, ...this.drawable]) {
      const p = pos[node.id]
      const g = this.el("g", { class: `gnodeg ${node.kind}`, tabindex: 0 })
      g.dataset.id = node.id
      g.append(this.el("rect", { x: p.x, y: p.y, width: NW, height: NH, rx: 8 }))
      if (node.kind !== "org") {
        g.append(this.textEl("glyph", p.x + 9, p.y + 12, GLYPHS[node.kind] || String(node.kind || "")))
      }
      g.append(this.textEl("lbl", p.x + 9, node.kind === "org" ? p.y + 19 : p.y + 23, this.truncate(this.label(node), 20)))
      if (node.kind !== "org" && node.kind !== "assets") {
        const count = this.presentCount(node.id)
        if (count) {
          const tt = Math.min(1, Math.max(0, (count - 1) / 9))
          g.append(this.el("circle", {
            cx: p.x + NW - 2, cy: p.y + 2, r: 8.5,
            style: `fill:hsl(${164 - tt * 12} ${44 + tt * 46}% ${61 - tt * 27}%)`,
          }))
          const badge = this.textEl("sigbadge-t", p.x + NW - 2, p.y + 5, String(count))
          badge.setAttribute("text-anchor", "middle")
          g.append(badge)
        }
      }
      g.addEventListener("click", (event) => { event.stopPropagation(); this.select(node.id) })
      nodeGroup.append(g)
      this.nodeEls[node.id] = g
    }

    this.svg.addEventListener("click", () => this.select("org"))
    this.canvasTarget.replaceChildren(this.svg)
  }

  downstream(id) {
    const seen = new Set()
    const queue = [id]
    while (queue.length) {
      const current = queue.shift()
      for (const next of this.adjacency[current] || []) {
        if (!seen.has(next)) { seen.add(next); queue.push(next) }
      }
    }
    return seen
  }

  // The reference's selection: the node .sel, its downstream subtree .down, edges within the
  // reachable set .hot, everything else dimmed by the has-sel rules.
  select(id) {
    const node = id === "org" ? this.orgNode : this.drawable.find(n => n.id === id)
    if (!node) return
    const down = this.downstream(id)
    this.svg.classList.add("has-sel")
    for (const el of Object.values(this.nodeEls)) el.classList.remove("sel", "down")
    this.nodeEls[id].classList.add("sel")
    for (const t of down) if (this.nodeEls[t]) this.nodeEls[t].classList.add("down")
    for (const { edge, path } of this.edgeEls) {
      path.classList.toggle("hot", (edge.from === id || down.has(edge.from)) && down.has(edge.to))
    }
    this.renderPanel(node)
  }

  // ---- signals per source ----

  signalsFor(sourceId) {
    return this.signals.filter(s => s.asserted_by_id === sourceId)
  }

  signalState(signal) {
    const value = signal.value && typeof signal.value === "object" ? signal.value : {}
    return value.state || "not_set"
  }

  presentCount(sourceId) {
    return this.signalsFor(sourceId).filter(s => {
      const state = this.signalState(s)
      return (state === "verified" || state === "guessed") && s.attribute !== "read_level"
    }).length
  }

  readLevel(sourceId) {
    const signal = this.signalsFor(sourceId).find(s => s.attribute === "read_level")
    if (!signal) return null
    const value = signal.value && typeof signal.value === "object" ? signal.value.value : null
    return value == null ? null : String(value)
  }

  // ---- the side panel, in the reference's markup ----

  renderPanel(node) {
    if (node.kind === "org") return this.organizationPanel()
    if (node.kind === "assets") return this.assetsPanel(node)
    if (node.kind === "directory") return this.directoryPanel(node)
    if (node.kind === "contact") {
      this.head("contact", node.locator || "", "contact")
      this.sideBd.innerHTML = `<div class="side-empty">A reporting contact. No signals of its own.</div>`
      return
    }
    this.sourcePanel(node)
  }

  head(kind, locator, sub) {
    this.sideHd.innerHTML = `<div class="k">${this.esc(kind)}</div>
      <div class="loc">${this.esc(locator)}</div>
      ${sub ? `<div class="sub">${this.esc(sub)}</div>` : ""}`
  }

  dl(pairs) {
    const rows = pairs
      .filter(([, v]) => v !== undefined && v !== null && v !== "")
      .map(([k, v]) => `<div class="r"><span class="kk">${this.esc(k)}</span><span class="vv">${this.esc(String(v))}</span></div>`)
    return rows.length ? `<div class="dl">${rows.join("")}</div>` : ""
  }

  sigList(signals) {
    if (!signals.length) return `<div class="side-empty">No signals asserted.</div>`
    const present = signals.filter(s => this.signalState(s) !== "not_set").length
    return `<div class="sig-count">${signals.length} signals — <b>${present}</b> present, ${signals.length - present} not set</div>` +
      signals.map(s => {
        const state = this.signalState(s)
        return `<div class="sigrow ${state === "not_set" ? "off" : ""}">
          <span class="st ${STATE_CLASS[state] || "n"}">${STATE_CHAR[state] || ""}</span>
          <span class="name">${this.esc(s.attribute || "")}</span>
          <span class="meta">${this.esc(STATE_LABEL[state] || state)}</span>
        </div>`
      }).join("")
  }

  organizationPanel() {
    this.head("organization", this.nameValue || "Organization", this.statusValue || "")
    let body = this.dl([["Level", this.levelValue], ["Status", this.statusValue]])

    const order = Object.keys(GROUP_LABELS)
    const kinds = [...new Set(this.drawable.map(n => n.kind))]
    kinds.sort((a, b) => {
      const ia = order.indexOf(a), ib = order.indexOf(b)
      return (ia === -1 ? order.length : ia) - (ib === -1 ? order.length : ib)
    })
    if (!kinds.length) {
      body += `<div class="side-empty">No resources discovered — nothing found beyond the domain.</div>`
    }
    for (const kind of kinds) {
      const members = this.drawable.filter(n => n.kind === kind)
      body += `<div class="divlabel">${this.esc(GROUP_LABELS[kind] || kind)} (${members.length})</div>`
      body += members.map(member => {
        const count = this.presentCount(member.id)
        return `<button type="button" class="resrow" data-goto="${this.esc(member.id)}">
          <span class="g2">${this.esc(GLYPHS[kind] || kind)}</span>
          <span class="rl">${this.esc(this.label(member))}</span>
          ${count ? `<span class="sc">${count} set →</span>` : `<span class="sc none">→</span>`}
        </button>`
      }).join("")
    }
    this.sideBd.innerHTML = body
    this.bindGoto()
  }

  sourcePanel(node) {
    const kind = String(node.kind || "").replace("security_", "")
    const signals = this.signalsFor(node.id).filter(s => s.attribute !== "read_level")
    const readLevel = this.readLevel(node.id)
    const aggregate = this.drawable.find(n => n.kind === "assets" && n.covererId === node.id)
    let sub = node.platform ? `hosted on ${node.platform}` : kind
    if (!node.platform && signals.length) sub += ` · read${readLevel ? ` · L${readLevel}` : ""}`
    if (aggregate) sub += ` · covers ${aggregate.members.length} assets`
    this.head(kind, node.locator || this.label(node), sub)
    this.sideBd.innerHTML = this.sigList(signals)
  }

  assetsPanel(node) {
    const scopeOf = (asset) => this.assetScope(asset)
    const items = (node.members || []).slice().sort((a, b) =>
      (scopeOf(a) === "in" ? 0 : 1) - (scopeOf(b) === "in" ? 0 : 1) ||
      String(a.locator || "").localeCompare(String(b.locator || "")))
    const inCount = items.filter(a => scopeOf(a) === "in").length
    const coverer = this.drawable.find(n => n.id === node.covererId)
    this.head("scope assets", node.locator, coverer ? `covered by ${this.label(coverer)}` : "organization assets")
    this.sideBd.innerHTML =
      `<div class="sig-count"><b>${inCount}</b> in scope${items.length - inCount ? `, ${items.length - inCount} out of scope` : ""}</div>` +
      items.map(a => {
        const scoped = scopeOf(a) === "in"
        return `<div class="sigrow"><span class="name">${this.esc(a.locator || "")}</span>
          <span class="scope ${scoped ? "in" : "out"}">${scoped ? "in scope" : "out of scope"}</span></div>`
      }).join("")
  }

  assetScope(asset) {
    if (asset.scope) return asset.scope
    const edge = this.storedEdges.find(e => e.relation === "covers" && e.to === asset.id)
    return edge && edge.data && edge.data.scope ? edge.data.scope : "in"
  }

  // The directory node carries the listing's state (MaturityDashboardHelper#drawable_graph puts it
  // there from the listing node, or from the stored edge of an older snapshot).
  directoryPanel(node) {
    const data = (node.listing && typeof node.listing === "object") ? node.listing : {}
    const loaded = Boolean(data.loaded_at)
    this.head("directory", node.locator || "",
      ["provided", node.published === undefined ? null : (node.published ? "published" : "unpublished")].filter(Boolean).join(" · "))
    const chip = loaded
      ? `<span class="scope in">loaded</span>`
      : `<span class="scope out">${data.rendering === "client" ? "declared · client-rendered" : "declared"}</span>`
    this.sideBd.innerHTML =
      this.dl([["Published", node.published === undefined ? null : (node.published ? "yes" : "no")]]) +
      `<div class="divlabel">this listing ${chip}</div>` +
      this.dl([
        ["listing_url", data.listing_url || "none"],
        ["rendering", data.rendering || "—"],
        ["loaded_at", loaded ? data.loaded_at : "—"],
      ])
  }

  bindGoto() {
    for (const row of this.sideBd.querySelectorAll("[data-goto]")) {
      row.addEventListener("click", () => this.select(row.dataset.goto))
    }
  }

  // ---- helpers ----

  label(node) {
    const locator = node.locator || node.attribute || node.id || ""
    try {
      const url = new URL(locator)
      return url.pathname.length > 1 ? url.pathname : url.host
    } catch {
      return String(locator)
    }
  }

  truncate(value, max) {
    const s = String(value)
    return s.length > max ? `${s.slice(0, max - 1)}…` : s
  }

  el(tag, attrs = {}) {
    const node = document.createElementNS(SVG, tag)
    for (const [k, v] of Object.entries(attrs)) node.setAttribute(k, v)
    return node
  }

  textEl(className, x, y, content) {
    const t = this.el("text", { class: className, x, y })
    t.textContent = content
    return t
  }

  esc(value) {
    const div = document.createElement("div")
    div.textContent = String(value)
    return div.innerHTML
  }
}
